import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getTokenAsync } from "../../utils/tokenManage";
import alertUtils from "../../utils/utils";

function Sidebar({ isOpen, onClose, currentPath = "" }) {
    const [menuList, setMenuList] = useState([]);
    const [openMenuIds, setOpenMenuIds] = useState(new Set());

    const navigate = useNavigate();

    const normalizedPath = useMemo(() => {
        if (!currentPath) return "";

        if (currentPath === "/notice") return "/view/app/notice";
        if (currentPath === "/history") return "/view/app/history";

        return currentPath;
    }, [currentPath]);

    useEffect(() => {
        getSidebarMenu();
    }, []);

    useEffect(() => {
        if (!menuList.length) return;

        const activeParentIds = findActiveParentIds(menuList, normalizedPath);
        setOpenMenuIds(new Set(activeParentIds));
    }, [menuList, normalizedPath]);

    const getSidebarMenu = async () => {
        try {
            const authToken = await getTokenAsync();

            const response = await fetch(
                `${(window.__env__?.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL)}/api/v1/token/main/menu`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authToken
                    },
                    credentials: "include"
                }
            );

            if (!response.ok) {
                throw new Error("사이드바 메뉴 조회 실패");
            }

            const data = await response.json();
            const treeData = buildMenuTree(data);
            setMenuList(treeData);
        } catch (error) {
            console.error("사이드바 메뉴 조회 오류:", error);
        }
    };

    const buildMenuTree = (flatList) => {
        const map = {};
        const roots = [];

        flatList.forEach((item) => {
            map[item.seq] = {
                ...item,
                menuName: (item.menuName || "").trim(),
                children: []
            };
        });

        flatList.forEach((item) => {
            const current = map[item.seq];
            const parent = map[item.seqUpper];

            if (Number(item.level) === 2) {
                roots.push(current);
            } else if (parent && Number(item.level) > 2) {
                parent.children.push(current);
            }
        });

        return roots;
    };

    const toggleMenu = (seq) => {
        setOpenMenuIds((prev) => {
            const next = new Set(prev);

            if (next.has(seq)) {
                next.delete(seq);
            } else {
                next.add(seq);
            }

            return next;
        });
    };

    const findActiveParentIds = (menus, path, parents = []) => {
        for (const menu of menus) {
            if (menu.url === path) {
                return parents;
            }

            if (menu.children?.length > 0) {
                const result = findActiveParentIds(
                    menu.children,
                    path,
                    [...parents, menu.seq]
                );

                if (result.length > 0 || menu.children.some((child) => child.url === path)) {
                    return result;
                }
            }
        }

        return [];
    };

    const isActiveMenu = (menu, path) => {
        if (menu.url === path) return true;
        return (menu.children || []).some((child) => isActiveMenu(child, path));
    };

    const handleMenuClick = (menu) => {
        const hasChildren = menu.children && menu.children.length > 0;

        if (hasChildren) {
            toggleMenu(menu.seq);
            return;
        }

        if (menu.url) {
            if (menu.url === "/view/app/notice") {
                navigate("/notice", { state: { refresh: true } });
            } else if (menu.url === "/view/app/history") {
                navigate("/history", { state: { refresh: true } });
            } else {
                navigate("/history", { state: { refresh: true } });
                alertUtils.info("사용할 수 없는 메뉴입니다.");
            }
        }

        if (onClose) {
            onClose();
        }
    };

    const renderMenuItems = (menus, depth = 1) => {
        return menus.map((menu) => {
            const hasChildren = menu.children?.length > 0;
            const isOpen = openMenuIds.has(menu.seq);
            const isActive = isActiveMenu(menu, normalizedPath);

            if (depth === 1) {
                return (
                    <div
                        key={menu.seq}
                        className={`nav-item ${isOpen ? "expanded" : ""}`}
                    >
                        <div
                            className={`nav-section ${isActive ? "nav-active" : ""}`}
                            onClick={() => handleMenuClick(menu)}
                        >
                            {menu.menuName}
                            {hasChildren && (
                                <span className="nav-caret">
                                    <i className="icon-w-arrow"></i>
                                </span>
                            )}
                        </div>

                        {hasChildren && isOpen && (
                            <div className="nav-sub-container">
                                {renderMenuItems(menu.children, depth + 1)}
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <div key={menu.seq}>
                    <div
                        className={`nav-sub ${menu.url === normalizedPath ? "nav-sub-active" : ""} ${hasChildren ? "has-children" : ""}`}
                        onClick={() => handleMenuClick(menu)}
                    >
                        {menu.menuName}
                        {hasChildren && (
                            <span className="nav-caret">
                                <i className="icon-w-arrow"></i>
                            </span>
                        )}
                    </div>

                    {hasChildren && isOpen && (
                        <div className="nav-sub-container sub-depth">
                            {renderMenuItems(menu.children, depth + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <>
            {isOpen && <div className="sidebar-dim" onClick={onClose}></div>}

            <aside className={`sidebar ${isOpen ? "open" : ""}`} id="sidebar">
                <nav className="nav">
                    {renderMenuItems(menuList, 1)}
                </nav>
            </aside>
        </>
    );
}

export default Sidebar;